import { Box, Button, Typography } from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from "react";
import hotkeys from "hotkeys-js";
import DialogClose from "../dialogClose/dialogClose";
import { DialogHeader, dialogPaperSx, pillButtonSx } from "../formKit/formKit";

type Content = {
  title?: string;
  options?: Options;
  actions?: string[];
};

type Options = {
  subtitle?: string | ReactNode;
  render?: (c: any) => ReactElement;
  icon?: ReactNode;
};

export const AskQuestionContext = createContext(
  (title: string, actions: string[], options?: Options | undefined | null) =>
    new Promise<number>((r) => r(1))
);

let resolve: (value: number | PromiseLike<number>) => void;

export default function AskQuestion({ children }: any) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<Content>({});

  hotkeys("enter", function (event, handler) {
    // Prevent the default refresh event under WINDOWS system
    event.preventDefault();
    setOpen(false);
    if (resolve) resolve(1);
  });

  const ask = (
    title: string,
    actions: string[],
    options?: Options | undefined | null
  ) => {
    setContent({
      title,
      actions,
      options,
    });
    setOpen(true);

    return new Promise<number>((r) => {
      resolve = r;
    });
  };

  return (
    <AskQuestionContext.Provider value={ask}>
      {children}
      <DialogClose
        open={open}
        PaperProps={{ sx: dialogPaperSx }}
        onClose={() => {
          setOpen(false);
          resolve(0);
        }}
      >
        {content.title ? (
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {content.options?.icon}
              <DialogHeader title={content.title} />
            </Box>
          </DialogTitle>
        ) : (
          []
        )}
        <DialogContent>
          <Typography>{content.options?.subtitle}</Typography>
          {content.options?.render ? (
            <Typography>
              {content.options.render(() => {
                setOpen(false);
                resolve(0);
              })}
            </Typography>
          ) : (
            []
          )}
        </DialogContent>
        {content.actions?.length ? (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {content.actions.map((a, i) => (
              <Button
                variant={
                  Number(i) === content.actions.length - 1 ? "solid" : "plain"
                }
                color={
                  Number(i) === content.actions.length - 1
                    ? "primary"
                    : "neutral"
                }
                sx={pillButtonSx}
                onClick={() => {
                  setOpen(false);
                  resolve(i);
                }}
                key={a}
              >
                {a}
              </Button>
            ))}
          </DialogActions>
        ) : (
          []
        )}
      </DialogClose>
    </AskQuestionContext.Provider>
  );
}

export const useAskQuestion = () => {
  const context = useContext(AskQuestionContext);
  return context;
};
